import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { searchPA, currencyFormat, addPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaCheck, 
  FaUsers, 
  FaCalendarAlt, 
  FaCog, 
  FaSpinner,
  FaCheckCircle,
  FaCreditCard
} from 'react-icons/fa'
import './SelectPlan.css'

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { users: plans, isAuthenticated, authenticateUser, setToken, authenticatedUser, setUsers, setAlert } = useUserContext()

  const [licensesNumber, setLicensesNumber] = useState(1)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [licensesError, setLicensesError] = useState('')
  const [currentStep, setCurrentStep] = useState(1)

  const handleChangeSelectedPlan = (planId) => {
    setSelectedPlan(planId)
  }
  const handleChangeLicensesNumber = (e) => {
    const inputValue = e.target.value
    const value = inputValue === '' ? 0 : parseInt(inputValue, 10)
    
    setLicensesNumber(value)
    
    // Validación en tiempo real
    if (value <= 0) {
      setLicensesError('El número de licencias debe ser mayor a 0')
    } else {
      setLicensesError('')
    }
  }

  // Wizard navigation functions
  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const goToStep = (step) => {
    // Solo permitir navegación a pasos completados o al paso actual
    if (step <= currentStep || (step === 2 && selectedPlan) || (step === 3 && selectedPlan && licensesNumber > 0 && !licensesError)) {
      setCurrentStep(step)
    }
  }

  const handleSentRequest = async (licensesNumber, selectedPlan) => {
    if(licensesNumber <= 0 || !selectedPlan) {
      setAlert({
        message: 'Por favor ingresa todos los datos requeridos',
        msgType: 'warning'
      })
      return
    }

    setIsLoading(true)
    
    try {
      let userData = await addPA({ 
        path: 'memberships',
        data: {
          userId: authenticateUser.userId,
          licensesNumber: licensesNumber,
          planId: selectedPlan,
        }, 
        token: authenticatedUser.value 
      })

      if(Object.prototype.hasOwnProperty.call(userData, 'planId')) {
        userData = {
          ...authenticatedUser,
          pendingPlan: false,
          sessionCode: false,
        }
        authenticateUser(userData)
        setToken(authenticateUser.value)
        navigate('/home')
      } else {
        setAlert({
          message: userData.message,
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al procesar la solicitud. Inténtalo de nuevo.',
        msgType: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fetchData = async () => {
    const response = await searchPA({ path: 'plans', token: authenticatedUser.value, pageSize: 100 })
    if (response.error || !Array.isArray(response.data)) {
      setUsers([])
      if(isAuthenticated) {
        setAlert({
          message: response.message || 'Error en la transacción. Si persiste el error, por favor contacte a soporte.',
          msgType: 'warning'
        })
      }
    } else {
      setUsers(response.data)
      // Preseleccionar el primer plan si no hay ninguno seleccionado
      if (!selectedPlan && response.data.length > 0) {
        setSelectedPlan(response.data[0].planId)
      }
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Preseleccionar el primer plan cuando se cargan los planes
  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      setSelectedPlan(plans[0].planId)
    }
  }, [plans, selectedPlan])

  const selectedPlanData = plans.find(plan => plan.planId === selectedPlan)
  const totalPrice = selectedPlanData ? selectedPlanData.price * licensesNumber : 0
  
  const isButtonDisabled = !selectedPlan || licensesNumber <= 0

  return (
    <>
      {(isAuthenticated) ? (
        <div className="select-plan-container">
          <div className="container">
            {/* Header */}
            <div className="select-plan-header">
              <div className="header-content">
                <div className="header-icon">
                  <FaCreditCard />
                </div>
                <div className="header-text">
                  <h1>Selecciona tu Plan</h1>
                  <p>Elige el plan que mejor se adapte a tus necesidades y configura el número de licencias</p>
                </div>
              </div>
            </div>

            {/* Wizard Progress */}
            <div className="wizard-progress">
              <div className="wizard-steps">
                <div 
                  className={`wizard-step ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : ''}`}
                  onClick={() => goToStep(1)}
                >
                  <div className="wizard-step-circle">1</div>
                  <div className="wizard-step-label">Elige tu plan</div>
                </div>
                <div 
                  className={`wizard-step ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : ''} ${!selectedPlan ? 'disabled' : ''}`}
                  onClick={() => goToStep(2)}
                >
                  <div className="wizard-step-circle">2</div>
                  <div className="wizard-step-label">Define tus licencias</div>
                </div>
                <div 
                  className={`wizard-step ${currentStep >= 3 ? (currentStep > 3 ? 'completed' : 'active') : ''} ${!(selectedPlan && licensesNumber > 0 && !licensesError) ? 'disabled' : ''}`}
                  onClick={() => goToStep(3)}
                >
                  <div className="wizard-step-circle">3</div>
                  <div className="wizard-step-label">Revisa tu resumen</div>
                </div>
                <div 
                  className={`wizard-step ${currentStep >= 4 ? 'active' : ''} ${!(selectedPlan && licensesNumber > 0 && !licensesError) ? 'disabled' : ''}`}
                  onClick={() => goToStep(4)}
                >
                  <div className="wizard-step-circle">4</div>
                  <div className="wizard-step-label">Finaliza tu pago</div>
                </div>
                <div 
                  className="wizard-progress-bar" 
                  style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Wizard Content */}
            <div className="wizard-content">
              {/* Step 1: Plan Selection */}
              <div className={`wizard-step-content wizard-step-1 ${currentStep === 1 ? 'active' : ''}`}>
                <div className="plans-grid">
                  {plans.map((plan) => (
                    <div 
                      key={plan.planId} 
                      className={`plan-card ${plan.planId === selectedPlan ? 'selected' : ''}`}
                    >
                      <div className="plan-card-header">
                        <h3 className="plan-name">{plan.planName}</h3>
                        <div className="plan-price">
                          {currencyFormat({ number: plan.price })}
                        </div>
                        <div className="plan-price-period">por licencia</div>
                      </div>

                      <div className="plan-features">
                        <div className="select-plan-description">
                          {plan.description}
                        </div>
                        
                        <div className="plan-features-list">
                          <div className="plan-feature">
                            <FaUsers className="plan-feature-icon" />
                            <span>Hasta {plan.maxGuests} invitados por sesión</span>
                          </div>
                          
                          <div className="plan-feature">
                            <FaCalendarAlt className="plan-feature-icon" />
                            <span>Sesiones ilimitadas</span>
                          </div>
                          
                          <div className="plan-feature">
                            <FaCog className="plan-feature-icon" />
                            <span>Gestión completa de contenido</span>
                          </div>
                        </div>
                      </div>

                      <button 
                        className={`plan-select-btn ${plan.planId === selectedPlan ? 'selected' : ''}`}
                        onClick={() => handleChangeSelectedPlan(plan.planId)}
                        disabled={plan.planId === selectedPlan}
                      >
                        {plan.planId === selectedPlan ? 'Seleccionado' : 'Seleccionar'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 2: Licenses Selection */}
              <div className={`wizard-step-content wizard-step-2 ${currentStep === 2 ? 'active' : ''}`}>
                <div className="licenses-section">
                  <div className="card-body">
                    <div className="licenses-content">
                      <div className="licenses-info">
                        <h3 className="licenses-title">Número de Licencias</h3>
                        <p className="licenses-subtitle">Selecciona cuántas licencias necesitas para tu plan</p>
                      </div>
                      <div className="licenses-input-container">
                        <label className="licenses-label">Cantidad:</label>
                        <input
                          type="number"
                          className={`licenses-input ${licensesError ? 'error' : ''}`}
                          value={licensesNumber}
                          onChange={handleChangeLicensesNumber}
                          min="1"
                        />
                      </div>
                    </div>
                    {licensesError && (
                      <div className="error-message" style={{ marginTop: '1rem', textAlign: 'center' }}>{licensesError}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 3: Summary */}
              <div className={`wizard-step-content wizard-step-3 ${currentStep === 3 ? 'active' : ''}`}>
                {selectedPlanData && (
                  <div className="summary-container">
                    <div className="summary-header">
                      <h3 className="summary-title">Resumen de tu Selección</h3>
                      <p className="summary-subtitle">Revisa los detalles antes de proceder al pago</p>
                    </div>
                    
                    <div className="summary-grid">
                      {/* Plan Details */}
                      <div className="summary-section">
                        <div className="summary-section-header">
                          <FaUsers className="summary-section-icon" />
                          <h4>Plan Seleccionado</h4>
                        </div>
                        <div className="summary-plan-card">
                          <div className="summary-plan-name">{selectedPlanData.planName}</div>
                          <div className="summary-plan-description">{selectedPlanData.description}</div>
                          <div className="summary-plan-features">
                            <div className="summary-feature">
                              <FaUsers className="summary-feature-icon" />
                              <span>Hasta {selectedPlanData.maxGuests} invitados por sesión</span>
                            </div>
                            <div className="summary-feature">
                              <FaCalendarAlt className="summary-feature-icon" />
                              <span>Sesiones ilimitadas</span>
                            </div>
                            <div className="summary-feature">
                              <FaCog className="summary-feature-icon" />
                              <span>Gestión completa de contenido</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Pricing Details */}
                      <div className="summary-section">
                        <div className="summary-section-header">
                          <FaCreditCard className="summary-section-icon" />
                          <h4>Detalles de Precio</h4>
                        </div>
                        <div className="summary-pricing-card">
                          <div className="pricing-row">
                            <span className="pricing-label">Precio por licencia:</span>
                            <span className="pricing-value">{currencyFormat({ number: selectedPlanData.price })}</span>
                          </div>
                          <div className="pricing-row">
                            <span className="pricing-label">Cantidad de licencias:</span>
                            <span className="pricing-value">{licensesNumber}</span>
                          </div>
                          <div className="pricing-row pricing-subtotal">
                            <span className="pricing-label">Subtotal:</span>
                            <span className="pricing-value">{currencyFormat({ number: totalPrice })}</span>
                          </div>
                          <div className="pricing-row pricing-total">
                            <span className="pricing-label">Total a pagar:</span>
                            <span className="pricing-value">{currencyFormat({ number: totalPrice })}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Summary Footer */}
                    <div className="summary-footer">
                      <div className="summary-footer-content">
                        <div className="summary-footer-icon">
                          <FaCheckCircle />
                        </div>
                        <div className="summary-footer-text">
                          <h5>¡Todo listo!</h5>
                          <p>Tu selección está completa. Procede al pago para activar tu plan.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 4: Payment */}
              <div className={`wizard-step-content wizard-step-4 ${currentStep === 4 ? 'active' : ''}`}>
                <div className="payment-section">
                  <div className="payment-info">
                    <h3>¡Listo para proceder al pago!</h3>
                    <p>Tu selección ha sido confirmada. Al hacer clic en "Proceder al Pago" serás redirigido para completar tu compra de forma segura.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Wizard Navigation */}
            <div className="wizard-navigation">
              <button 
                className="wizard-nav-btn secondary"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                Anterior
              </button>
              
              {currentStep < 4 ? (
                <button 
                  className="wizard-nav-btn"
                  onClick={nextStep}
                  disabled={
                    (currentStep === 1 && !selectedPlan) ||
                    (currentStep === 2 && (licensesNumber <= 0 || licensesError))
                  }
                >
                  Siguiente
                </button>
              ) : (
                <button 
                  className={`wizard-nav-btn ${isLoading ? 'loading' : ''}`}
                  onClick={() => handleSentRequest(licensesNumber, selectedPlan)}
                  disabled={isLoading || isButtonDisabled}
                >
                  {isLoading ? (
                    <>
                      <FaSpinner />
                      Procesando...
                    </>
                  ) : (
                    'Proceder al Pago'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        navigate('/login')
      )}
    </>
  )
}
